import React from 'react';
import { Form, Formik } from 'formik';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { Appointment } from '../../interfaces';
import FullPageModal from '../modal/FullPageModal';
import ClientSearchAutofill from '../form/ClientSearchAutofill';
import CheckoutDetails from '../form/CheckoutDetails';
import CheckoutFormItems from '../form/CheckoutFormItems';
import { getServiceName } from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import PaymentMethodsList from './PaymentMethodsList';
import { CheckoutPaymentMethod } from '../../types/appointments';
import { formatDollarAmount, noop } from '../../utils/global/helper';
import CheckoutFormPricingTable from '../form/CheckoutFormPricingTable';
import ValuesListener from '../form/ValuesListener';
import useDepositCheckoutSession from '../../hooks/appointments/useDepositCheckoutSession';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { getAppointmentDepositCheckoutSession } from '../../services/appointments';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';

interface DepositCheckoutFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  appointment: Appointment;
}

function DepositCheckoutForm({
  isOpen,
  onClose,
  onSuccess,
  appointment,
}: DepositCheckoutFormProps) {
  const { user } = useUser();
  const {
    setTerminalCheckoutModalToLoading: setTerminalCheckoutLoading,
    setMostRecentPaymentId,
    resetCheckoutFailureData,
  } = useCheckoutForm();

  const initialValues = React.useMemo(
    () => ({
      appointmentId: appointment.appointmentId,
      employeeId: appointment.employeeId,
      depositPrice: appointment.depositPrice || 0,
      paymentMethod: 'TERMINAL',
      customer: {
        customerId: appointment.customerId,
        firstName: appointment.customer.firstName,
        lastName: appointment.customer.lastName,
        email: appointment.customer.email,
        phoneNumber: appointment.customer.phoneNumber,
        dob: appointment.customer.dob?.split('T')[0],
      },
    }),
    [appointment],
  );

  const [formValues, setFormValues] =
    React.useState<typeof initialValues>(initialValues);

  const debouncedDepositPrice = useDebouncedValue(formValues.depositPrice, 250);
  const isDebouncing = formValues.depositPrice !== debouncedDepositPrice;

  const { checkoutSession, isFetching } = useDepositCheckoutSession({
    appointment,
    depositPrice: Number(debouncedDepositPrice),
    status: 'schedule',
    enabled: isOpen,
    isTerminal: true,
  });
  const isLoading = isFetching || isDebouncing;

  const onSubmit = React.useCallback(
    async ({ depositPrice }: typeof initialValues) => {
      if (isLoading || !checkoutSession || depositPrice <= 0) return;

      resetCheckoutFailureData();

      const { totalPriceCents, paymentIntent, paymentMethod } =
        await getAppointmentDepositCheckoutSession({
          appointmentId: appointment.appointmentId,
          status: 'schedule',
          depositPrice,
          isTerminal: true,
        });

      if (
        !paymentIntent ||
        totalPriceCents !== checkoutSession.totalPriceCents ||
        paymentMethod !== 'TERMINAL'
      )
        return;

      setTerminalCheckoutLoading();
      setMostRecentPaymentId(paymentIntent.paymentId);
      toast.success('Charge sent to terminal');

      onClose();

      await onSuccess();
    },
    [
      appointment.appointmentId,
      checkoutSession,
      isLoading,
      onClose,
      onSuccess,
      resetCheckoutFailureData,
      setMostRecentPaymentId,
      setTerminalCheckoutLoading,
    ],
  );

  return (
    <FullPageModal
      modalOpen={isOpen}
      setModalOpen={onClose}
      handleClose={onClose}
      name="Deposit Checkout">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={undefined}
        onSubmit={onSubmit}>
        {({ isSubmitting, setFieldValue, values, isValid }) => (
          <>
            <ValuesListener debounceTime={250} onChange={setFormValues} />

            <ClientSearchAutofill
              title="Client & Appointment"
              hideBirthday
              namePrefix={['customer']}
              selectedRequest={appointment}
              disableCustomerEdit
            />
            <Form
              style={{
                paddingTop: 0,
              }}>
              <CheckoutDetails appointment={appointment} />
              <CheckoutFormItems
                items={[
                  {
                    id: appointment.appointmentId,
                    name: `${getServiceName(appointment, user)} Deposit`,
                    price: values.depositPrice,
                    quantity: 1,
                    onPriceChange: (value: number) =>
                      setFieldValue('depositPrice', value),
                  },
                ]}
              />

              <CheckoutFormPricingTable
                preSubtotalItems={[
                  {
                    label: 'Price',
                    value: values.depositPrice,
                  },
                ]}
                postSubtotalItems={[
                  {
                    label: 'Sales tax',
                    value: (checkoutSession?.salesTaxCents ?? 0) / 100,
                    visible: !!checkoutSession?.salesTaxCents,
                  },
                  {
                    label: 'Convenience fee',
                    value: (checkoutSession?.serviceFeeCents ?? 0) / 100,
                    visible: !!checkoutSession?.serviceFeeCents,
                  },
                ]}
                total={(checkoutSession?.totalPriceCents ?? 0) / 100}
                subtotal={values.depositPrice}
                isLoading={isLoading}
              />

              <div className="input-group">
                <Typography variant="h2">Payment Method</Typography>
                <PaymentMethodsList
                  paymentMethods={['TERMINAL']}
                  selectedPaymentMethod={
                    values.paymentMethod as CheckoutPaymentMethod
                  }
                  onChange={noop}
                />
              </div>

              <LoadingButton
                type="submit"
                disabled={
                  isLoading ||
                  !isValid ||
                  isSubmitting ||
                  !values.paymentMethod ||
                  Number(values.depositPrice) <= 0
                }
                variant="contained"
                className="primary"
                loading={isSubmitting}>
                <span>
                  Request{' '}
                  {formatDollarAmount(
                    (checkoutSession?.totalPriceCents ?? 0) / 100,
                    true,
                  )}
                </span>
              </LoadingButton>
            </Form>
          </>
        )}
      </Formik>
    </FullPageModal>
  );
}

export default DepositCheckoutForm;
